import type { Product } from '../product.js';
import type { PackageState } from '../schemas/plan.js';

/**
 * The schedule layer's types (planning-app-information-architecture.md §6.2–§6.4, v3.10). Plain
 * types, no zod: the engine runs in the importer, in `check` and in the browser; the file's zod
 * schema is `schemas/schedule.ts`.
 */

export type Pacing = 'conservative' | 'compressed';
export const PACINGS = ['conservative', 'compressed'] as const;

/** An external date the plan cannot move (§6.3 *Fixed*): OAuth verification, a release train, a month-end, a sunset. */
export interface FixedDate {
  id: string;
  date: string;
  kind: string;
  label?: string;
  /** The packages that cannot end before the date. */
  affects: string[];
  source?: string;
}

/** `docs/overview/plan-schedule.json` — plan, in git, edited only through plan-change MRs. */
export interface ScheduleFile {
  version: 1;
  anchor: { wave0Start: string };
  pacing: Pacing;
  gates?: Record<string, { target?: string }>;
  phases?: Record<string, { overlapWeeks?: number; minWaitWeeks?: number }>;
  packages?: Record<string, { week?: string; weeks?: number }>;
  tracks?: Record<string, { parked?: boolean; until?: string; reason?: string; focus?: boolean; shift?: Record<string, number> }>;
  waves?: Record<string, { shift?: number }>;
  fixed?: FixedDate[];
}

export interface SchedulePhase {
  id: string;
  label: string;
  name: string;
  conservativeWeeks: [number, number] | null;
  compressedWeeks: [number, number] | null;
  /** Phase 5's buckets: every one starts at the last serial phase's gate (§6.2). */
  concurrent: boolean;
}

export interface SchedulePackage {
  id: string;
  unit: string;
  unitName: string;
  lane: string;
  product: Product | null;
  phase: string;
  wave: number;
  weight: number;
  state: PackageState;
  fourEyes: boolean;
  reviewers: string[];
  /** The earliest phase an open prerequisite decision blocks — the fill's second key after the wave. */
  blocksPhase: string | null;
  parentId: string | null;
  /** The week a locked package (`building` or later) already holds; the fill keeps it. */
  plannedWeek: string | null;
  plannedWeeks: number | null;
  /** The open decisions gating the package (§6.4 *Decision lead time*, v3.11b); absent where the caller has none (the `check` script). */
  openPrerequisites?: OpenPrerequisite[];
}

/** An open prerequisite decision as the lead-time rule reads it: when it must be decided by, and what the record says about when it will be. */
export interface OpenPrerequisite {
  id: string;
  title: string;
  owner: { id: string; name: string } | null;
  /** `decisions_state.deadline` or the BP3 window's end; null when none. */
  deadline: string | null;
  /** The first scheduled session the decision is pinned to; null when none. */
  sessionAt: string | null;
}

export interface ScheduleTail {
  id: string;
  label: string;
  weeks: number;
  /** The phase whose gate the tail runs from. */
  from: string;
  text: string;
}

/** What the engine reads of the spine: the phases with their clocks, the packages, the package-to-package edges, the tails. */
export interface ScheduleSpine {
  phases: SchedulePhase[];
  packages: SchedulePackage[];
  /** `[from, to]`: `to` cannot start before `from` ends (critical path, contracts first, Wave 0 first, a sub-package after its parent). */
  edges: [string, string][];
  tails: ScheduleTail[];
}

export interface ScheduleOptions {
  /** The Brain line and the programme line per week, in points, on the compressed pacing; the conservative pacing scales them by `conservativeFactor` (BP12). */
  linePlatform?: number;
  lineTotal?: number;
  /** What the conservative pacing multiplies the two lines by (IA §6.3 says halved); one constant so the rule can flip. */
  conservativeFactor?: number;
  /** Weeks a package spans by weight 1…5. */
  weeksByWeight?: readonly number[];
  focusMax?: number;
  /** Four-eyes or L-and-above packages one week may hold per reviewer role (§6.4, `SCHEDULE_FOUR_EYES_PER_WEEK`). */
  fourEyesPerWeek?: number;
  /** Weeks before a package's planned week by which its open prerequisites must be decided (§6.4, `SCHEDULE_DECISION_LEAD_WEEKS`). */
  decisionLeadWeeks?: number;
}

/* ---------- The derived schedule ---------- */

export interface PhaseWindow {
  phase: string;
  label: string;
  name: string;
  /** Week indexes from the anchor: `[start, end)`; the gate sits at `end`. */
  start: number;
  end: number;
  /** The clock range's upper bound for this pacing. */
  length: number;
  /** First week after the last scheduled package (`start` when nothing is scheduled). */
  buildEnd: number;
  /** `end − buildEnd`, hatched on the calendar; zero when the build fills the window. */
  waitWeeks: number;
  floorWait: number;
  overlap: number;
  gate: { week: number; date: string; source: 'derived' | 'override' };
  concurrent: boolean;
}

export interface Placement {
  id: string;
  phase: string;
  unit: string;
  /** Week index from the anchor and its ISO label. */
  week: number;
  weekLabel: string;
  weeks: number;
  source: 'derived' | 'override' | 'locked';
}

export interface WeekLoad {
  week: number;
  weekLabel: string;
  hire: number;
  /** The Platform line's points (product `platform`); `brain` until v3.16 (HB24 D3) — `legacy.ts` reads the stored shape. */
  platform: number;
  seam: number;
  total: number;
  overPlatform: boolean;
  overTotal: boolean;
}

export interface DerivedPacing {
  pacing: Pacing;
  lines: { platform: number; total: number };
  windows: PhaseWindow[];
  placements: Placement[];
  /** Per week from the anchor to the last gate, the points scheduled by product against the two lines. */
  capacity: WeekLoad[];
  /** The last gate's week index. */
  end: number;
}

export interface DerivedSchedule {
  /** The anchor's Monday. */
  anchor: string;
  pacing: Pacing;
  planned: DerivedPacing;
  ghost: DerivedPacing;
  /** Tracks parked by the file: their unbuilt packages are off the calendar. */
  parked: { unit: string; until: string | null; reason: string | null }[];
  focus: string[];
  fixed: (FixedDate & { week: number })[];
  tails: (ScheduleTail & { start: number; end: number })[];
}

/* ---------- Edits (the operations of §6.3; staged by v3.11) ---------- */

export type ScheduleEdit =
  | { op: 'schedule-package'; id: string; week: string; weeks?: number }
  | { op: 'shift-track-phase'; unit: string; phase: string; weeks: number }
  | { op: 'shift-wave'; wave: number; weeks: number }
  | { op: 'gate-target'; phase: string; date: string }
  | { op: 'park-track'; unit: string; until?: string; reason: string }
  | { op: 'unpark-track'; unit: string }
  | { op: 'focus-track'; unit: string; focus: boolean };

export const SCHEDULE_OPS = ['schedule-package', 'shift-track-phase', 'shift-wave', 'gate-target', 'park-track', 'unpark-track', 'focus-track'] as const;
export const isScheduleEdit = (e: { op: string }): e is ScheduleEdit => (SCHEDULE_OPS as readonly string[]).includes(e.op);

/** The hard rules (§6.4): a drop snaps back, a merged file fails the import, `check` fails the MR. */
export const SCHEDULE_RULES = ['prerequisite-order', 'phase-order', 'gate-order', 'product-edge', 'fixed-date', 'before-anchor', 'locked', 'unknown'] as const;
export type ScheduleHardRule = (typeof SCHEDULE_RULES)[number];
/** The soft rules (§6.4, v3.11b): the drop lands, amber, with the figure. */
export const SCHEDULE_SOFT_RULES = ['capacity', 'four-eyes', 'dark-inventory', 'decision-lead', 'parked-prerequisite'] as const;
export type ScheduleSoftRule = (typeof SCHEDULE_SOFT_RULES)[number];
export type ScheduleRule = ScheduleHardRule | ScheduleSoftRule;

export interface ScheduleViolation {
  rule: ScheduleRule;
  /** The package, phase, wave, week or decision the rule failed on. */
  subject: string;
  /** What it failed against: a prerequisite, a gate, a fixed date, a capacity line. */
  against: string | null;
  message: string;
  /** The week index the breach sits in, when it is a week's (capacity, four-eyes). */
  week?: number;
  /** The figure the amber chip shows: what was measured against which line. */
  figure?: { value: number; line: number };
}

export interface ScheduleCheck {
  hard: ScheduleViolation[];
  soft: ScheduleViolation[];
}

/* ---------- Implications (§6.5; v3.11b) ---------- */

export interface PlacementChange {
  id: string;
  unit: string;
  phase: string;
  before: { week: number; weekLabel: string; weeks: number } | null;
  after: { week: number; weekLabel: string; weeks: number } | null;
  /** Named by an edit (true) or moved because one of its prerequisites did (false). */
  direct: boolean;
}

export interface GateChange {
  phase: string;
  label: string;
  before: string;
  after: string;
  deltaWeeks: number;
  /** The following phase's start, before → after; null for the last phase. */
  nextStart: { phase: string; label: string; before: string; after: string } | null;
}

export interface DecisionBy {
  id: string;
  title: string;
  owner: { id: string; name: string } | null;
  /** The packages whose lead time sets the date. */
  packages: string[];
  /** The latest date it must be decided by (the earliest package's week less the lead). */
  by: string;
  deadline: string | null;
  sessionAt: string | null;
  /** The deadline, the BP3 window or the session falls after `by`. */
  late: boolean;
}

export interface ScheduleImplications {
  /** The schedule edits the implications are of. */
  edits: ScheduleEdit[];
  hard: ScheduleViolation[];
  /** The soft breaches the edits introduce, or change (a re-dated or re-figured one); empty with no edits — at rest nothing is introduced, every breach of the schedule stands. */
  soft: ScheduleViolation[];
  /** The breaches of the schedule before the edits that the edits clear (the rule no longer fails on that subject and counterpart). */
  softResolved: ScheduleViolation[];
  /** The breaches of the schedule before the edits that stand word for word after them; with no edits, every breach of the schedule as it stands. */
  softStanding: ScheduleViolation[];
  /** Every placement whose week or span changed, the edits' own first, then their dependents, each *before → after*. */
  moved: PlacementChange[];
  /** The latest-ending package before and after (the long pole of the fill). */
  longPole: { before: string | null; after: string | null; changed: boolean };
  /** Every phase, with its gate date before → after and the next phase's start. */
  gates: GateChange[];
  /** The weeks whose load changed or sits over a line after the edits, with both loads. */
  capacity: { week: number; weekLabel: string; before: WeekLoad; after: WeekLoad }[];
  lines: { platform: number; total: number };
  /** The open prerequisites of the moved and dependent packages with their *must be decided by* dates. */
  decisionsBy: DecisionBy[];
  /** The programme end on both pacings, before → after. */
  clock: { planned: { pacing: Pacing; before: string; after: string }; ghost: { pacing: Pacing; before: string; after: string } };
  parked: { before: string[]; after: string[] };
  focus: { before: string[]; after: string[] };
}
